import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import { useKakaoMap } from "^/common/useKakaoMap";
import {
  useCompanyMapList,
  type CompanyType,
} from "~/services/customer/home";
import { IconSearch } from "@/Icon/Search";
import { IconMungSmall } from "@/Icon/MungSmall";
import { LocationPermissionDialog } from "@/LocationPermissionDialog";
import {
  COMPANY_CATEGORIES as CATEGORIES,
  providesCategory,
} from "%/companyCategories";
import {
  requestDeviceLocation,
  type DeviceLocationError,
} from "%/nativeLocation";
import {
  loadMapViewState,
  saveMapViewState,
} from "../../../stores/mapViewState";

export const Route = createFileRoute("/customer/layout/map")({
  component: MapPage,
});

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 };

// 가맹점(activeYn=Y) 마커용 멍투데이 로고 (IconMungSmall과 동일한 발바닥 SVG)
const MUNG_PAW_SVG = `
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99996 16.3535C11.623 16.3535 12.1426 17.0829 13.6033 17.0829C15.0644 17.0829 15.9734 16.1103 15.9734 14.408C15.9734 12.7056 14.058 11.7938 13.2135 10.6383C12.3694 9.48319 11.7854 8.17578 9.99965 8.17578C8.21394 8.17578 7.62957 9.48319 6.78544 10.6383C5.94131 11.7935 4.02588 12.7054 4.02588 14.408C4.02588 16.1103 4.93487 17.0829 6.39596 17.0829C7.85705 17.0832 8.37656 16.3535 9.99996 16.3535Z" fill="#ED8432"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.1819 2.526C14.1884 2.71736 14.7451 3.90966 14.4263 5.18994C14.1071 6.46994 13.033 7.35259 12.0271 7.1615C11.0209 6.97014 10.4638 5.77783 10.7827 4.49756C11.1018 3.21783 12.1759 2.3349 13.1819 2.526Z" fill="#ED8432"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M6.38383 2.526C5.3779 2.71736 4.8206 3.90966 5.13972 5.18994C5.45856 6.46994 6.53298 7.35259 7.53891 7.1615C8.54512 6.97014 9.10214 5.77783 8.78302 4.49756C8.4639 3.21783 7.39004 2.3349 6.38383 2.526Z" fill="#ED8432"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M2.93124 6.43094C1.97752 6.61194 1.44938 7.74254 1.75163 8.95571C2.05388 10.1692 3.07214 11.006 4.02618 10.8247C4.9799 10.6437 5.50804 9.51278 5.20547 8.29961C4.90322 7.08644 3.88496 6.24964 2.93124 6.43094Z" fill="#ED8432"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.0685 6.43094C18.0222 6.61194 18.5507 7.74254 18.2481 8.95571C17.9458 10.1692 16.9273 11.006 15.9736 10.8247C15.0198 10.6437 14.4917 9.51278 14.7943 8.29961C15.0965 7.08644 16.1151 6.24964 17.0685 6.43094Z" fill="#ED8432"/>
  </svg>`;

const SHEET_COLLAPSED_H = 88; // 핸들 + 주소 한 줄만 보이는 높이
const SHEET_EXPANDED_RATIO = 0.7; // 화면 높이의 70%까지 펼쳐짐

/** idle 이후 이만큼 더 조용해야 재검색 — 연속 제스처를 한 번으로 묶는다 */
const IDLE_DEBOUNCE_MS = 250;

/** 주소→좌표 결과 캐시. 자동 재검색으로 마커를 자주 다시 그리므로 세션 내내 재사용한다 */
const geocodeCache = new Map<string, { lat: number; lng: number }>();

/** 이 거리 안에서 누르고 뗐을 때만 마커 '탭'으로 인정 (손떨림 허용치) */
const TAP_SLOP_PX = 10;
/** 길게 누르고 있다 떼는 것도 탭으로 보지 않는다 */
const TAP_MAX_MS = 600;

const navigateToCompanyDetail = (
  push: (path: string) => void,
  company: CompanyType
) => {
  const params = new URLSearchParams({
    companySeq: company.companySeq.toString(),
  });
  if (company.masterYn) params.append("masterYn", company.masterYn);
  push(`/customer/layout/home-serach-detail?${params.toString()}`);
};

function MapPage() {
  const { history } = useRouter();
  const { ready, error } = useKakaoMap();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const markerGenerationRef = useRef(0);
  // 마커를 누른 뒤 떼기 전까지 지도가 움직였는지 — 드래그 끝에 딸려오는 click 을 걸러낸다
  const mapDraggedRef = useRef(false);
  const geocoderRef = useRef<any>(null);
  const locationOverlayRef = useRef<any>(null);
  const locationRefreshInFlightRef = useRef(false);
  const [isLocating, setIsLocating] = useState(false);

  // 상세 페이지에서 뒤로 왔을 때 마지막으로 보던 지도 위치를 복원 (마운트 시 1회만 읽음)
  const savedViewRef = useRef(loadMapViewState());
  const savedView = savedViewRef.current;
  // 복원된 상태로 시작하면 최초 위치 조회 결과로 지도를 재센터링하지 않는다
  // (내 위치 버튼을 직접 누르면 해제됨)
  const suppressGeoRecenterRef = useRef(!!savedView);

  const [categoryValue, setCategoryValue] = useState<string | null>(
    savedView?.category ?? null
  );
  const [currentAddress, setCurrentAddress] = useState<string>("");
  const [bounds, setBounds] = useState<{
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
  } | null>(null);

  // 지도 이동/확대축소가 멎으면 자동 재검색한다. 아래 ref 들은 그 과정에서
  // 불필요한 요청을 막기 위한 것 — 마지막으로 조회한 뷰포트와 디바운스 타이머.
  const lastSyncedRef = useRef<{
    level: number;
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
  } | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 다른 이펙트/핸들러에서도 호출할 수 있게 지도 초기화 이펙트가 여기에 넣어둔다
  const syncViewportRef = useRef<(() => void) | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  // 시트 높이는 window 가 아니라 실제 렌더 영역(카드 안 컨테이너) 기준으로 계산
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerH, setContainerH] = useState(600);
  const sheetExpandedH = Math.round(containerH * SHEET_EXPANDED_RATIO);
  const sheetTravel = sheetExpandedH - SHEET_COLLAPSED_H;
  const dragControls = useDragControls();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setContainerH(el.clientHeight);
      mapInstanceRef.current?.relayout?.();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const categoryValueRef = useRef(categoryValue);
  categoryValueRef.current = categoryValue;

  // 페이지를 떠날 때(상세 이동 등) 현재 지도 상태를 저장 → 뒤로가기로 돌아오면 복원
  useEffect(() => {
    return () => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const center = map.getCenter();
      saveMapViewState({
        lat: center.getLat(),
        lng: center.getLng(),
        level: map.getLevel(),
        category: categoryValueRef.current,
      });
    };
  }, []);

  // 현재 위치 기반 초기 중심. undefined = 위치 조회 중, null = 실패/거부(디폴트 좌표 사용)
  const [geoCenter, setGeoCenter] = useState<
    { lat: number; lng: number } | null | undefined
  >(undefined);
  const [locationError, setLocationError] =
    useState<DeviceLocationError | null>(null);

  useEffect(() => {
    let canceled = false;
    // 지도 첫 진입은 위치 기능을 실제로 사용하는 시점이므로 권한 요청을 표시한다.
    // 이후 거부된 경우에는 내 위치/거리순 버튼에서만 다시 요청한다.
    requestDeviceLocation({ interactive: true }).then(({ location }) => {
      if (canceled) return;
      setGeoCenter(location ? { lat: location.lat, lng: location.lng } : null);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const initialCenter = savedView ?? geoCenter ?? DEFAULT_CENTER;

  const { data: companyList, isFetching: isFetchingCompanies } =
    useCompanyMapList(
      {
        swLat: bounds?.swLat ?? 0,
        swLng: bounds?.swLng ?? 0,
        neLat: bounds?.neLat ?? 0,
        neLng: bounds?.neLng ?? 0,
        category: categoryValue,
      },
      !!bounds
    );

  // 바텀시트 리스트는 가맹점(activeYn=Y)이 먼저 나오도록 정렬 (sort는 stable이라 나머지 순서 유지)
  const sortedCompanyList = useMemo(() => {
    if (!companyList) return companyList;
    return [...companyList].sort(
      (a: CompanyType, b: CompanyType) =>
        (b.activeYn === "Y" ? 1 : 0) - (a.activeYn === "Y" ? 1 : 0)
    );
  }, [companyList]);

  useEffect(() => {
    if (!ready || !mapRef.current || mapInstanceRef.current) return;
    const kakao = window.kakao;
    const map = new kakao.maps.Map(mapRef.current, {
      center: new kakao.maps.LatLng(initialCenter.lat, initialCenter.lng),
      level: savedView?.level ?? 4,
    });
    mapInstanceRef.current = map;
    geocoderRef.current = new kakao.maps.services.Geocoder();

    // 내 위치 파란 점 — 위치 조회 성공 시에만 (폴백 좌표에는 표시하지 않음)
    if (geoCenter) {
      const dot = document.createElement("div");
      dot.className = "h-4 w-4 rounded-full border-2 border-white shadow";
      dot.style.backgroundColor = "#4A90E2";
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(geoCenter.lat, geoCenter.lng),
        content: dot,
      });
      overlay.setMap(map);
      locationOverlayRef.current = overlay;
    }

    const updateBoundsFromMap = () => {
      const b = map.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      setBounds({
        swLat: sw.getLat(),
        swLng: sw.getLng(),
        neLat: ne.getLat(),
        neLng: ne.getLng(),
      });
    };

    const updateRegionFromCenter = () => {
      const center = map.getCenter();
      geocoderRef.current.coord2RegionCode(
        center.getLng(),
        center.getLat(),
        (result: any[], status: string) => {
          if (status !== kakao.maps.services.Status.OK) return;
          const region = result.find((r) => r.region_type === "H") ?? result[0];
          if (!region) return;
          setCurrentAddress(region.address_name);
        }
      );
    };

    /**
     * 현재 뷰포트를 조회 조건에 반영한다.
     * 확대/축소가 바뀌었거나 중심이 화면의 10% 이상 움직였을 때만 실제로 갱신 —
     * 관성 스크롤 잔떨림이나 미세한 손떨림까지 매번 재조회하지 않기 위한 문턱값이다.
     */
    const syncViewport = (force = false) => {
      const b = map.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const next = {
        level: map.getLevel(),
        swLat: sw.getLat(),
        swLng: sw.getLng(),
        neLat: ne.getLat(),
        neLng: ne.getLng(),
      };

      const prev = lastSyncedRef.current;
      if (!force && prev && prev.level === next.level) {
        const latSpan = next.neLat - next.swLat;
        const lngSpan = next.neLng - next.swLng;
        const dLat = Math.abs(
          (next.swLat + next.neLat) / 2 - (prev.swLat + prev.neLat) / 2
        );
        const dLng = Math.abs(
          (next.swLng + next.neLng) / 2 - (prev.swLng + prev.neLng) / 2
        );
        if (dLat <= latSpan * 0.1 && dLng <= lngSpan * 0.1) return;
      }

      lastSyncedRef.current = next;
      updateBoundsFromMap();
      updateRegionFromCenter();
    };
    syncViewportRef.current = () => syncViewport(true);

    syncViewport(true);

    // 마커 위에서 시작한 드래그/핀치도 지도를 움직이는데, 이때 발생하는 click 은 탭이 아니다.
    // (플래그는 마커 pointerdown 에서 매 제스처마다 초기화된다)
    kakao.maps.event.addListener(map, "dragstart", () => {
      mapDraggedRef.current = true;
    });
    kakao.maps.event.addListener(map, "zoom_changed", () => {
      mapDraggedRef.current = true;
    });

    // idle = 드래그·확대축소·setCenter 등 모든 이동이 멎은 뒤 한 번 발생.
    // dragend/zoom_changed 를 따로 듣지 않아도 되고, 연속 제스처는 디바운스로 묶는다.
    kakao.maps.event.addListener(map, "idle", () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => syncViewport(), IDLE_DEBOUNCE_MS);
    });
  }, [ready, initialCenter, geoCenter, savedView]);

  useEffect(
    () => () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    },
    []
  );

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!ready || !map || !geoCenter) return;
    const kakao = window.kakao;
    const position = new kakao.maps.LatLng(geoCenter.lat, geoCenter.lng);

    if (locationOverlayRef.current) {
      locationOverlayRef.current.setMap(null);
      locationOverlayRef.current = null;
    }

    const dot = document.createElement("div");
    dot.className = "h-4 w-4 rounded-full border-2 border-white shadow";
    dot.style.backgroundColor = "#4A90E2";
    const overlay = new kakao.maps.CustomOverlay({
      position,
      content: dot,
    });
    overlay.setMap(map);
    locationOverlayRef.current = overlay;

    // 복원된 지도 위치를 보는 중에는 내 위치 점만 찍고 재센터링하지 않는다
    if (suppressGeoRecenterRef.current) return;

    map.setCenter(position);
    // 문턱값을 무시하고 즉시 반영 (내 위치 버튼은 명시적 의사표시)
    syncViewportRef.current?.();
  }, [ready, geoCenter]);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current) return;
    const kakao = window.kakao;
    const map = mapInstanceRef.current;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const source: CompanyType[] = companyList ?? [];

    const placeMarker = (company: CompanyType, lat: number, lng: number) => {
      const markerColor =
        CATEGORIES.find((c) => providesCategory(company, c))?.color ??
        CATEGORIES.find((c) => c.value === categoryValue)?.color ??
        "#FF9934";
      const pos = new kakao.maps.LatLng(lat, lng);
      const content = document.createElement("div");
      content.className = "relative flex flex-col items-center";
      // 가맹점(activeYn=Y)은 멍투데이 로고 마커, 그 외는 카테고리 색상 점 마커
      const markerDot =
        company.activeYn === "Y"
          ? `<div class="flex h-7 w-7 items-center justify-center rounded-full bg-white border-2 border-[#ED8432] shadow">${MUNG_PAW_SVG}</div>`
          : `<div class="w-5 h-5 rounded-full border-2 border-white shadow" style="background-color:${markerColor}"></div>`;
      content.innerHTML = `
        <div class="max-w-[140px] px-2 py-0.5 mb-0.5 text-[11px] font-bold text-black bg-white rounded-md shadow-sm text-center leading-tight break-keep">
          ${company.companyName}
        </div>
        ${markerDot}
      `;
      // 마커 오버레이는 이벤트를 가로채지 않아서, 마커 위에서 시작하거나 끝난 드래그도
      // 손을 뗄 때 click 이 그대로 발생한다 → 지도만 옮기려다 상세로 튕겨 나갔다.
      // "누른 지점에서 거의 움직이지 않고, 그 사이 지도가 움직이지도 않은" 입력만 탭으로 인정한다.
      let downX = Number.NaN;
      let downY = Number.NaN;
      let downAt = 0;
      content.addEventListener("pointerdown", (e) => {
        downX = e.clientX;
        downY = e.clientY;
        downAt = e.timeStamp;
        mapDraggedRef.current = false;
      });
      content.addEventListener("click", (e) => {
        // pointerdown 이 이 마커에서 시작되지 않았다면(다른 곳에서 끌어와 마커 위에서 뗌) 무시
        if (Number.isNaN(downX)) return;
        if (mapDraggedRef.current) return;
        if (
          Math.abs(e.clientX - downX) > TAP_SLOP_PX ||
          Math.abs(e.clientY - downY) > TAP_SLOP_PX
        )
          return;
        if (e.timeStamp - downAt > TAP_MAX_MS) return;
        navigateToCompanyDetail(history.push, company);
      });
      const overlay = new kakao.maps.CustomOverlay({
        position: pos,
        content,
        yAnchor: 1,
      });
      overlay.setMap(map);
      markersRef.current.push(overlay);
    };

    // 자동 재검색이라 이 이펙트가 자주 돈다. 늦게 도착한 지오코딩 콜백이
    // 이미 지워진 세대의 마커를 다시 꽂지 않도록 세대 번호로 막는다.
    markerGenerationRef.current += 1;
    const generation = markerGenerationRef.current;

    source.forEach((company: CompanyType) => {
      if (
        typeof company.latitude === "number" &&
        typeof company.longitude === "number"
      ) {
        placeMarker(company, company.latitude, company.longitude);
        return;
      }
      if (!company.companyAddr) return;

      // 주소→좌표는 결과가 바뀌지 않으므로 화면 갱신마다 다시 물어볼 이유가 없다
      const cached = geocodeCache.get(company.companyAddr);
      if (cached) {
        placeMarker(company, cached.lat, cached.lng);
        return;
      }
      geocoderRef.current.addressSearch(
        company.companyAddr,
        (result: any[], status: string) => {
          if (status !== kakao.maps.services.Status.OK || !result[0]) return;
          const lat = Number(result[0].y);
          const lng = Number(result[0].x);
          geocodeCache.set(company.companyAddr!, { lat, lng });
          if (generation !== markerGenerationRef.current) return;
          placeMarker(company, lat, lng);
        }
      );
    });
  }, [ready, companyList, categoryValue, history]);

  // 내 위치로 재센터링 후 해당 영역 재검색
  const handleMoveToMyLocation = async () => {
    if (locationRefreshInFlightRef.current) return;

    locationRefreshInFlightRef.current = true;
    setIsLocating(true);
    try {
      const result = await requestDeviceLocation({
        forceRefresh: true,
        interactive: true,
      });
      const location = result.location;
      if (location) {
        suppressGeoRecenterRef.current = false;
        setGeoCenter({ lat: location.lat, lng: location.lng });
        return;
      }

      setLocationError(result.error);
    } finally {
      locationRefreshInFlightRef.current = false;
      setIsLocating(false);
    }
  };

  return (
    // -mb-24: 레이아웃 래퍼의 nav 여백(pb-24)을 회수해 지도를 main 바닥까지 풀블리드로.
    // nav 클리어런스는 시트 하단 88px 흰 판이 담당
    <div
      ref={containerRef}
      className="relative flex-auto min-h-0 -mb-24 w-full overflow-hidden bg-[#F5F5F5]"
    >
      <div className="absolute inset-x-0 top-0 z-20 bg-[#F5F5F5]/95 backdrop-blur px-4 pt-4 pb-2">
        <button
          type="button"
          onClick={() => history.push("/customer/layout/search")}
          className="relative flex h-11 w-full items-center rounded-full bg-white px-4 shadow-sm"
        >
          <span className="flex-1 text-left text-sm text-gray">
            매장 이름으로 검색
          </span>
          <IconSearch />
        </button>

        <div className="-mx-4 mt-3 overflow-x-auto">
          <div className="flex gap-2 px-4">
            {CATEGORIES.map((cat) => {
              const active = categoryValue === cat.value;
              return (
                <button
                  key={cat.label}
                  type="button"
                  onClick={() => setCategoryValue(cat.value)}
                  className={`flex h-10 shrink-0 items-center gap-1 rounded-full border-2 px-4 text-sm font-bold bg-white ${
                    active
                      ? "border-yellow text-yellow"
                      : "border-[#E5E5E5] text-black"
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div ref={mapRef} className="absolute inset-0" />

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#F5F5F5]">
          <div className="text-sm text-gray">
            {error ? error.message : "지도 로딩 중..."}
          </div>
        </div>
      )}

      {/* 재검색 버튼 대신 자동 조회 중임을 알리는 표시만 남긴다 */}
      {isFetchingCompanies && ready && (
        <div className="absolute left-1/2 top-[148px] z-20 -translate-x-1/2 rounded-full bg-white/95 px-4 h-9 text-sm font-bold shadow-md flex items-center gap-2">
          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#FF9934] border-t-transparent" />
          이 지역 검색 중…
        </div>
      )}

      {/* 지도 SDK 준비 전에도 노출: 클릭하면 위치 권한 요청부터 진행한다. */}
      <button
        type="button"
        onClick={handleMoveToMyLocation}
        disabled={isLocating}
        aria-label={isLocating ? "현재 위치 확인 중" : "내 위치로 이동"}
        aria-busy={isLocating}
        className="absolute right-4 bottom-[192px] z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md disabled:cursor-wait disabled:opacity-60"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className={isLocating ? "animate-pulse" : undefined}
        >
          <circle cx="12" cy="12" r="6" stroke="#4A90E2" strokeWidth="1.8" />
          <circle cx="12" cy="12" r="2" fill="#4A90E2" />
          <path
            d="M12 3V6M12 18V21M3 12H6M18 12H21"
            stroke="#4A90E2"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <LocationPermissionDialog
        error={locationError}
        onClose={() => setLocationError(null)}
      />

      {/* nav 뒤에 깔리는 흰 배경판: 시트가 닿지 않는 88px 영역을 시트와 같은 흰색으로 메워서
          nav 뒤로 지도가 비치지 않게 한다. z-10이라 nav(z-50)에는 가려지지 않고 지도(z-0) 위에만 깔린다. */}
      <div className="absolute inset-x-0 bottom-0 z-10 h-[88px] bg-white" />

      <motion.div
        className="absolute inset-x-0 bottom-[88px] z-20 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.08)] rounded-t-2xl flex flex-col"
        style={{ height: sheetExpandedH, touchAction: "none" }}
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: sheetTravel }}
        dragElastic={0.05}
        initial={{ y: sheetTravel }}
        animate={{ y: sheetOpen ? 0 : sheetTravel }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onDragEnd={(_, info) => {
          if (info.velocity.y < -300 || info.offset.y < -80) {
            setSheetOpen(true);
          } else if (info.velocity.y > 300 || info.offset.y > 80) {
            setSheetOpen(false);
          }
        }}
      >
        <div
          className="select-none cursor-grab active:cursor-grabbing"
          style={{ touchAction: "none" }}
          onPointerDown={(e) => dragControls.start(e)}
          onClick={() => setSheetOpen((v) => !v)}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full bg-[#E0E0E0]" />
          </div>
          <div className="flex items-center gap-2 px-4 py-3">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 21C12 21 19 14.5 19 10C19 6.13401 15.866 3 12 3C8.13401 3 5 6.13401 5 10C5 14.5 12 21 12 21Z"
                stroke="#FF9934"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <circle
                cx="12"
                cy="10"
                r="2.5"
                stroke="#FF9934"
                strokeWidth="1.5"
              />
            </svg>
            <span className="flex-1 text-sm font-bold truncate">
              {currentAddress || "지역을 불러오는 중..."}
            </span>
            <span className="text-xs text-gray">
              {companyList?.length ?? 0}곳
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6">
          {(!companyList || companyList.length === 0) && (
            <div className="flex h-32 items-center justify-center text-sm text-gray">
              이 지역에 등록된 매장이 없어요
            </div>
          )}
          {sortedCompanyList?.map((company: CompanyType) => {
            const categoryHit = CATEGORIES.find((c) =>
              providesCategory(company, c)
            );
            return (
              <button
                key={`${company.masterYn ?? "N"}-${company.companySeq}`}
                type="button"
                onClick={() => navigateToCompanyDetail(history.push, company)}
                className="flex w-full items-center gap-3 border-b border-[#F0F0F0] py-3 text-left"
              >
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#F5F5F5]">
                  {company.fileUrl ? (
                    <img
                      src={company.fileUrl}
                      alt={company.companyName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span
                      className="absolute inset-0 m-auto h-3 w-3 rounded-full"
                      style={{
                        backgroundColor: categoryHit?.color ?? "#CCCCCC",
                      }}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    {company.activeYn === "Y" && (
                      <span className="shrink-0" aria-label="가맹점">
                        <IconMungSmall size={14} />
                      </span>
                    )}
                    <span className="truncate text-sm font-bold">
                      {company.companyName}
                    </span>
                    {CATEGORIES.filter((c) => c.value).map((c) => {
                      if (!providesCategory(company, c)) return null;
                      return (
                        <span
                          key={c.label}
                          title={c.label}
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white"
                          style={{ backgroundColor: c.color }}
                        >
                          {c.label.slice(0, 1)}
                        </span>
                      );
                    })}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray">
                    {company.companyAddr}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
